import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import contractService from '../../services/contractService';
import type {
  Contract,
  ContractParty,
  ContractComment,
  ContractStatus,
  ContractListParams,
  CreateContractData,
  UpdateContractData,
  ContractDashboardStats,
  ContractMilestone,
  ApprovalAction,
  DigitalSignatureData,
} from '../../types/contracts';

// Transform MongoDB contract document to frontend format
const transformContract = (contract: any): Contract => {
  const transformObjectId = (value: any) =>
    typeof value === 'object' && value?.$oid ? value.$oid : value;

  const transformDate = (value: any) =>
    typeof value === 'object' && value?.$date ? value.$date : value;

  return {
    ...contract,
    _id: transformObjectId(contract._id),
    id: transformObjectId(contract._id),
    projectId: transformObjectId(contract.projectId),
    contractTypeId: transformObjectId(contract.contractTypeId),
    partyId: transformObjectId(contract.partyId),
    createdBy: transformObjectId(contract.createdBy),
    updatedBy: transformObjectId(contract.updatedBy),
    startDate: transformDate(contract.startDate),
    endDate: transformDate(contract.endDate),
    signedDate: transformDate(contract.signedDate),
    createdAt: transformDate(contract.createdAt),
    updatedAt: transformDate(contract.updatedAt),
    milestones:
      contract.milestones?.map((milestone: any) => ({
        ...milestone,
        _id: transformObjectId(milestone._id),
        dueDate: transformDate(milestone.dueDate),
        completedDate: transformDate(milestone.completedDate),
        createdAt: transformDate(milestone.createdAt),
        updatedAt: transformDate(milestone.updatedAt),
      })) || [],
    collaborators: contract.collaborators?.map(transformObjectId) || [],
    watchers: contract.watchers?.map(transformObjectId) || [],
  };
};

// Transform contract party
const transformContractParty = (party: any) => ({
  ...party,
  _id: party._id?.$oid || party._id,
  id: party._id?.$oid || party._id,
  createdAt: party.createdAt?.$date || party.createdAt,
  updatedAt: party.updatedAt?.$date || party.updatedAt,
});

// Transform contract comment
const transformContractComment = (comment: any) => ({
  ...comment,
  _id: comment._id?.$oid || comment._id,
  id: comment._id?.$oid || comment._id,
  contractId: comment.contractId?.$oid || comment.contractId,
  authorId: comment.authorId?.$oid || comment.authorId,
  parentCommentId: comment.parentCommentId?.$oid || comment.parentCommentId,
  createdAt: comment.createdAt?.$date || comment.createdAt,
  updatedAt: comment.updatedAt?.$date || comment.updatedAt,
  editedAt: comment.editedAt?.$date || comment.editedAt,
});

interface ContractsState {
  // Contract list state
  contracts: Contract[];
  currentContract: Contract | null;
  loading: boolean;
  error: string | null;

  // Pagination and filtering
  currentPage: number;
  totalPages: number;
  totalContracts: number;
  query: ContractListParams;

  // Contract parties
  contractParties: ContractParty[];
  partiesLoading: boolean;
  selectedParty: ContractParty | null;

  // Comments
  comments: ContractComment[];
  commentsLoading: boolean;

  // Dashboard stats
  dashboardStats: ContractDashboardStats | null;
  statsLoading: boolean;

  // UI states
  showCreateModal: boolean;
  showSignatureModal: boolean;
  showApprovalModal: boolean;
  selectedMilestone: ContractMilestone | null;

  // Bulk operations
  selectedContractIds: string[];
  bulkOperationLoading: boolean;
}

const initialState: ContractsState = {
  contracts: [],
  currentContract: null,
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalContracts: 0,
  query: {
    page: 1,
    limit: 10,
    status: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
  contractParties: [],
  partiesLoading: false,
  selectedParty: null,
  comments: [],
  commentsLoading: false,
  dashboardStats: null,
  statsLoading: false,
  showCreateModal: false,
  showSignatureModal: false,
  showApprovalModal: false,
  selectedMilestone: null,
  selectedContractIds: [],
  bulkOperationLoading: false,
};

// Async thunks for API calls
export const fetchContracts = createAsyncThunk(
  'contracts/fetchContracts',
  async ({ projectId, params }: { projectId: string; params?: ContractListParams }) => {
    const response = await contractService.getContractsByProject(projectId, params);
    return {
      contracts: response.contracts?.map(transformContract) || [],
      pagination: response.pagination,
      stats: response.stats,
    };
  }
);

export const fetchAllContracts = createAsyncThunk(
  'contracts/fetchAllContracts',
  async (params?: ContractListParams) => {
    const response = await contractService.getAllContracts(params);
    return {
      contracts: response.contracts?.map(transformContract) || [],
      pagination: response.pagination,
      stats: response.stats,
    };
  }
);

export const fetchContractById = createAsyncThunk(
  'contracts/fetchContractById',
  async (contractId: string) => {
    const contract = await contractService.getContractById(contractId);
    return transformContract(contract);
  }
);

export const createContract = createAsyncThunk(
  'contracts/createContract',
  async (contractData: CreateContractData) => {
    const contract = await contractService.createContract(contractData);
    return transformContract(contract);
  }
);

export const updateContract = createAsyncThunk(
  'contracts/updateContract',
  async ({ contractId, data }: { contractId: string; data: UpdateContractData }) => {
    const contract = await contractService.updateContract(contractId, data);
    return transformContract(contract);
  }
);

export const deleteContract = createAsyncThunk(
  'contracts/deleteContract',
  async (contractId: string) => {
    await contractService.deleteContract(contractId);
    return contractId;
  }
);

export const duplicateContract = createAsyncThunk(
  'contracts/duplicateContract',
  async (contractId: string) => {
    const contract = await contractService.duplicateContract(contractId);
    return transformContract(contract);
  }
);

// Milestone management
export const addMilestone = createAsyncThunk(
  'contracts/addMilestone',
  async ({
    contractId,
    milestone,
  }: {
    contractId: string;
    milestone: Partial<ContractMilestone>;
  }) => {
    const contract = await contractService.addMilestone(contractId, milestone);
    return transformContract(contract);
  }
);

export const updateMilestone = createAsyncThunk(
  'contracts/updateMilestone',
  async ({
    contractId,
    milestoneId,
    data,
  }: {
    contractId: string;
    milestoneId: string;
    data: Partial<ContractMilestone>;
  }) => {
    const contract = await contractService.updateMilestone(contractId, milestoneId, data);
    return transformContract(contract);
  }
);

// Approval workflow
export const processApproval = createAsyncThunk(
  'contracts/processApproval',
  async ({ contractId, action }: { contractId: string; action: ApprovalAction }) => {
    const contract = await contractService.processApproval(contractId, action);
    return transformContract(contract);
  }
);

// Digital signature
export const addSignature = createAsyncThunk(
  'contracts/addSignature',
  async ({
    contractId,
    signatureData,
  }: {
    contractId: string;
    signatureData: DigitalSignatureData;
  }) => {
    const contract = await contractService.addSignature(contractId, signatureData);
    return transformContract(contract);
  }
);

// Contract parties
export const fetchContractParties = createAsyncThunk(
  'contracts/fetchContractParties',
  async (params?: any) => {
    const response = await contractService.getContractParties(params);
    return {
      parties: response.parties?.map(transformContractParty) || [],
      pagination: response.pagination,
    };
  }
);

export const createContractParty = createAsyncThunk(
  'contracts/createContractParty',
  async (partyData: any) => {
    const party = await contractService.createContractParty(partyData);
    return transformContractParty(party);
  }
);

// Comments
export const fetchContractComments = createAsyncThunk(
  'contracts/fetchContractComments',
  async ({ contractId, params }: { contractId: string; params?: any }) => {
    const response = await contractService.getContractComments(contractId, params);
    return response.comments?.map(transformContractComment) || [];
  }
);

export const addContractComment = createAsyncThunk(
  'contracts/addContractComment',
  async ({ contractId, comment }: { contractId: string; comment: any }) => {
    const newComment = await contractService.addContractComment(contractId, comment);
    return transformContractComment(newComment);
  }
);

// Dashboard stats
export const fetchDashboardStats = createAsyncThunk(
  'contracts/fetchDashboardStats',
  async (projectId: string) => {
    return await contractService.getDashboardStats(projectId);
  }
);

// Bulk operations
export const bulkUpdateStatus = createAsyncThunk(
  'contracts/bulkUpdateStatus',
  async ({ contractIds, status }: { contractIds: string[]; status: ContractStatus }) => {
    const result = await contractService.bulkUpdateStatus(contractIds, status);
    return { result, contractIds, status };
  }
);

// Contract slice
const contractsSlice = createSlice({
  name: 'contracts',
  initialState,
  reducers: {
    // UI state management
    setShowCreateModal: (state, action: PayloadAction<boolean>) => {
      state.showCreateModal = action.payload;
    },
    setShowSignatureModal: (state, action: PayloadAction<boolean>) => {
      state.showSignatureModal = action.payload;
    },
    setShowApprovalModal: (state, action: PayloadAction<boolean>) => {
      state.showApprovalModal = action.payload;
    },
    setSelectedMilestone: (state, action: PayloadAction<ContractMilestone | null>) => {
      state.selectedMilestone = action.payload;
    },

    // Query and filtering
    setQuery: (state, action: PayloadAction<Partial<ContractListParams>>) => {
      state.query = { ...state.query, ...action.payload };
    },
    resetQuery: state => {
      state.query = initialState.query;
    },

    // Selection management
    toggleContractSelection: (state, action: PayloadAction<string>) => {
      const contractId = action.payload;
      const index = state.selectedContractIds.indexOf(contractId);
      if (index > -1) {
        state.selectedContractIds.splice(index, 1);
      } else {
        state.selectedContractIds.push(contractId);
      }
    },
    selectAllContracts: state => {
      state.selectedContractIds = state.contracts.map(contract => contract.id);
    },
    clearContractSelection: state => {
      state.selectedContractIds = [];
    },

    // Error handling
    clearError: state => {
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    // Current contract management
    clearCurrentContract: state => {
      state.currentContract = null;
    },

    // Selected party management
    setSelectedParty: (state, action: PayloadAction<ContractParty | null>) => {
      state.selectedParty = action.payload;
    },
  },
  extraReducers: builder => {
    // Fetch contracts
    builder
      .addCase(fetchContracts.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContracts.fulfilled, (state, action) => {
        state.loading = false;
        state.contracts = action.payload.contracts;
        state.currentPage = action.payload.pagination?.page || 1;
        state.totalPages = action.payload.pagination?.pages || 1;
        state.totalContracts = action.payload.pagination?.total || 0;
      })
      .addCase(fetchContracts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch contracts';
      });

    // Fetch all contracts (global)
    builder
      .addCase(fetchAllContracts.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllContracts.fulfilled, (state, action) => {
        state.loading = false;
        state.contracts = action.payload.contracts;
        state.currentPage = action.payload.pagination?.page || 1;
        state.totalPages = action.payload.pagination?.pages || 1;
        state.totalContracts = action.payload.pagination?.total || 0;
      })
      .addCase(fetchAllContracts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch all contracts';
      });

    // Fetch contract by ID
    builder
      .addCase(fetchContractById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContractById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentContract = action.payload;
      })
      .addCase(fetchContractById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch contract';
      });

    // Create contract
    builder
      .addCase(createContract.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createContract.fulfilled, (state, action) => {
        state.loading = false;
        state.contracts.unshift(action.payload);
        state.currentContract = action.payload;
        state.showCreateModal = false;
        state.totalContracts += 1;
      })
      .addCase(createContract.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create contract';
      });

    // Update contract
    builder
      .addCase(updateContract.fulfilled, (state, action) => {
        const index = state.contracts.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.contracts[index] = action.payload;
        }
        if (state.currentContract?.id === action.payload.id) {
          state.currentContract = action.payload;
        }
      })
      .addCase(updateContract.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update contract';
      });

    // Delete contract
    builder
      .addCase(deleteContract.fulfilled, (state, action) => {
        state.contracts = state.contracts.filter(c => c.id !== action.payload);
        state.totalContracts = Math.max(0, state.totalContracts - 1);
        if (state.currentContract?.id === action.payload) {
          state.currentContract = null;
        }
      })
      .addCase(deleteContract.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete contract';
      });

    // Duplicate contract
    builder
      .addCase(duplicateContract.fulfilled, (state, action) => {
        state.contracts.unshift(action.payload);
        state.totalContracts += 1;
      })
      .addCase(duplicateContract.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to duplicate contract';
      });

    // Add/Update milestone
    builder
      .addCase(addMilestone.fulfilled, (state, action) => {
        if (state.currentContract?.id === action.payload.id) {
          state.currentContract = action.payload;
        }
        const contractIndex = state.contracts.findIndex(c => c.id === action.payload.id);
        if (contractIndex !== -1) {
          state.contracts[contractIndex] = action.payload;
        }
      })
      .addCase(updateMilestone.fulfilled, (state, action) => {
        if (state.currentContract?.id === action.payload.id) {
          state.currentContract = action.payload;
        }
        const contractIndex = state.contracts.findIndex(c => c.id === action.payload.id);
        if (contractIndex !== -1) {
          state.contracts[contractIndex] = action.payload;
        }
        state.selectedMilestone = null;
      });

    // Process approval
    builder
      .addCase(processApproval.fulfilled, (state, action) => {
        if (state.currentContract?.id === action.payload.id) {
          state.currentContract = action.payload;
        }
        const contractIndex = state.contracts.findIndex(c => c.id === action.payload.id);
        if (contractIndex !== -1) {
          state.contracts[contractIndex] = action.payload;
        }
        state.showApprovalModal = false;
      })
      .addCase(processApproval.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to process approval';
      });

    // Add signature
    builder
      .addCase(addSignature.fulfilled, (state, action) => {
        if (state.currentContract?.id === action.payload.id) {
          state.currentContract = action.payload;
        }
        const contractIndex = state.contracts.findIndex(c => c.id === action.payload.id);
        if (contractIndex !== -1) {
          state.contracts[contractIndex] = action.payload;
        }
        state.showSignatureModal = false;
      })
      .addCase(addSignature.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to add signature';
      });

    // Contract parties
    builder
      .addCase(fetchContractParties.pending, state => {
        state.partiesLoading = true;
      })
      .addCase(fetchContractParties.fulfilled, (state, action) => {
        state.partiesLoading = false;
        state.contractParties = action.payload.parties;
      })
      .addCase(fetchContractParties.rejected, (state, action) => {
        state.partiesLoading = false;
        state.error = action.error.message || 'Failed to fetch contract parties';
      });

    builder.addCase(createContractParty.fulfilled, (state, action) => {
      state.contractParties.unshift(action.payload);
    });

    // Comments
    builder
      .addCase(fetchContractComments.pending, state => {
        state.commentsLoading = true;
      })
      .addCase(fetchContractComments.fulfilled, (state, action) => {
        state.commentsLoading = false;
        state.comments = action.payload;
      })
      .addCase(fetchContractComments.rejected, (state, action) => {
        state.commentsLoading = false;
        state.error = action.error.message || 'Failed to fetch comments';
      });

    builder.addCase(addContractComment.fulfilled, (state, action) => {
      state.comments.push(action.payload);
    });

    // Dashboard stats
    builder
      .addCase(fetchDashboardStats.pending, state => {
        state.statsLoading = true;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.dashboardStats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.error = action.error.message || 'Failed to fetch dashboard stats';
      });

    // Bulk operations
    builder
      .addCase(bulkUpdateStatus.pending, state => {
        state.bulkOperationLoading = true;
      })
      .addCase(bulkUpdateStatus.fulfilled, (state, action) => {
        state.bulkOperationLoading = false;
        const { contractIds, status } = action.payload;

        // Update status for affected contracts
        state.contracts = state.contracts.map(contract =>
          contractIds.includes(contract.id) ? { ...contract, status } : contract
        );

        // Clear selection after bulk operation
        state.selectedContractIds = [];
      })
      .addCase(bulkUpdateStatus.rejected, (state, action) => {
        state.bulkOperationLoading = false;
        state.error = action.error.message || 'Failed to update contract status';
      });
  },
});

export const {
  setShowCreateModal,
  setShowSignatureModal,
  setShowApprovalModal,
  setSelectedMilestone,
  setQuery,
  resetQuery,
  toggleContractSelection,
  selectAllContracts,
  clearContractSelection,
  clearError,
  setError,
  clearCurrentContract,
  setSelectedParty,
} = contractsSlice.actions;

export default contractsSlice.reducer;
